import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ShoppingBag, Search, ChevronLeft, ChevronRight, Check, X, Package, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import PageHeader from '../../components/common/PageHeader';
import { useAuth } from '../../context/AuthContext';
import { functions, db } from '../../lib/firebase';

// ---------- Types ----------
type RedemptionStatus = 'pending' | 'approved' | 'fulfilled' | 'rejected';

interface Redemption {
  id: string;
  clientName: string;
  clientEmail: string;
  giftCard: string;
  points: number;
  valueCents: number;
  status: RedemptionStatus;
  date: string;
}

const STATUS_STYLES: Record<RedemptionStatus, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
  approved: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  fulfilled: 'bg-green-500/20 text-green-400 border border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border border-red-500/30',
};

const PAGE_SIZE = 10;

const LoyaltyRedemptionsPage: React.FC = () => {
  const { companyId } = useAuth();

  // Data state
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RedemptionStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // ---------- Fetch redemptions from Firestore ----------
  const fetchRedemptions = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);

    try {
      const redemptionsQuery = query(
        collection(db, 'loyalty_redemptions'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(redemptionsQuery);

      const items: Redemption[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt?.toDate?.() || null;
        return {
          id: doc.id,
          clientName: (data.clientDisplayName as string) || 'Unknown',
          clientEmail: (data.clientEmail as string) || '',
          giftCard: (data.giftCardCategory as string) || '--',
          points: (data.pointsSpent as number) || 0,
          valueCents: Math.round(((data.pointsSpent as number) || 0) / 10 * 100),
          status: (data.status as RedemptionStatus) || 'pending',
          date: createdAt ? createdAt.toISOString().split('T')[0] : '',
        };
      });

      setRedemptions(items);
    } catch (err: any) {
      console.error('[LoyaltyRedemptionsPage] Failed to fetch redemptions:', err);
      setError(err?.message || 'Failed to load redemptions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchRedemptions();
  }, [fetchRedemptions]);

  // ---------- Filter + paginate ----------
  const filteredRedemptions = useMemo(() => {
    let result = redemptions;
    if (statusFilter !== 'all') {
      result = result.filter((r) => r.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.clientName.toLowerCase().includes(q) ||
          r.clientEmail.toLowerCase().includes(q) ||
          r.giftCard.toLowerCase().includes(q)
      );
    }
    // Sort: pending first, then by date descending
    result = [...result].sort((a, b) => {
      const statusOrder: Record<RedemptionStatus, number> = { pending: 0, approved: 1, fulfilled: 2, rejected: 3 };
      if (statusOrder[a.status] !== statusOrder[b.status]) return statusOrder[a.status] - statusOrder[b.status];
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
    return result;
  }, [redemptions, statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredRedemptions.length / PAGE_SIZE));
  const paginatedRedemptions = filteredRedemptions.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ---------- Action handler ----------
  const handleAction = async (id: string, action: 'approve' | 'fulfill' | 'reject') => {
    if (!companyId) return;

    setProcessingId(id);

    try {
      const processRedemptionFn = httpsCallable<
        { redemptionId: string; companyId: string; action: 'approve' | 'fulfill' | 'reject'; rejectionReason?: string; fulfillmentNotes?: string },
        { success: boolean; redemptionId: string; newStatus: string }
      >(functions, 'processRedemption');

      await processRedemptionFn({
        redemptionId: id,
        companyId,
        action,
      });

      // Refresh the list after successful action
      await fetchRedemptions();
    } catch (err: any) {
      console.error(`[LoyaltyRedemptionsPage] Failed to ${action} redemption:`, err);
      setError(err?.message || `Failed to ${action} redemption. Please try again.`);
    } finally {
      setProcessingId(null);
    }
  };

  const pendingCount = redemptions.filter((r) => r.status === 'pending').length;
  const approvedCount = redemptions.filter((r) => r.status === 'approved').length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ShoppingBag}
        title="Redemption Requests"
        subtitle="Review and process gift card redemption requests from loyalty clients"
        badge={pendingCount > 0 ? `${pendingCount} Pending` : undefined}
        onRefresh={fetchRedemptions}
        isRefreshing={loading}
      />

      {/* Loading State */}
      {loading && redemptions.length === 0 && (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-12 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          <p className="text-sm text-gray-400">Loading redemption requests...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-gray-800/50 border border-red-500/30 rounded-xl p-8 flex flex-col items-center justify-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-400" />
          <p className="text-sm text-red-300">{error}</p>
          <button
            onClick={() => { setError(null); fetchRedemptions(); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors mt-2"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}

      {/* Content (show when not in initial loading or error-only state) */}
      {(!loading || redemptions.length > 0) && !error && (
        <>
          {/* Summary Badges */}
          <div className="flex flex-wrap gap-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="w-2 h-2 rounded-full bg-yellow-400" />
              <span className="text-sm text-yellow-400 font-medium">{pendingCount} Pending</span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="w-2 h-2 rounded-full bg-blue-400" />
              <span className="text-sm text-blue-400 font-medium">{approvedCount} Approved</span>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by client, email, or gift card..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as any); setCurrentPage(1); }}
              className="px-4 py-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Redemptions Table */}
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
            {filteredRedemptions.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                {redemptions.length === 0 ? (
                  <div className="space-y-2">
                    <ShoppingBag className="w-8 h-8 mx-auto text-gray-600" />
                    <p>No redemption requests yet</p>
                    <p className="text-xs text-gray-500">Redemption requests from loyalty clients will appear here</p>
                  </div>
                ) : (
                  <p>No redemption requests match your search criteria</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-400">
                  <thead className="text-xs text-gray-400 uppercase bg-gray-700/50">
                    <tr>
                      <th scope="col" className="px-6 py-3">Client</th>
                      <th scope="col" className="px-6 py-3">Gift Card</th>
                      <th scope="col" className="px-6 py-3 text-right">Points</th>
                      <th scope="col" className="px-6 py-3 text-right">Value</th>
                      <th scope="col" className="px-6 py-3">Status</th>
                      <th scope="col" className="px-6 py-3">Date</th>
                      <th scope="col" className="px-6 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRedemptions.map((req) => (
                      <tr key={req.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-white">{req.clientName}</div>
                          <div className="text-xs text-gray-500">{req.clientEmail}</div>
                        </td>
                        <td className="px-6 py-4">{req.giftCard}</td>
                        <td className="px-6 py-4 text-right font-medium text-white">{req.points.toLocaleString()}</td>
                        <td className="px-6 py-4 text-right font-medium text-white">
                          ${(req.valueCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-2.5 py-0.5 text-xs font-semibold uppercase rounded-full ${STATUS_STYLES[req.status]}`}>
                            {req.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {req.date
                            ? new Date(req.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                            : '--'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5">
                            {req.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleAction(req.id, 'approve')}
                                  disabled={processingId === req.id}
                                  title="Approve"
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {processingId === req.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Check className="w-3.5 h-3.5" />
                                  )}
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleAction(req.id, 'reject')}
                                  disabled={processingId === req.id}
                                  title="Reject"
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {processingId === req.id ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <X className="w-3.5 h-3.5" />
                                  )}
                                  Reject
                                </button>
                              </>
                            )}
                            {req.status === 'approved' && (
                              <button
                                onClick={() => handleAction(req.id, 'fulfill')}
                                disabled={processingId === req.id}
                                title="Mark as Fulfilled"
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {processingId === req.id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Package className="w-3.5 h-3.5" />
                                )}
                                Fulfill
                              </button>
                            )}
                            {(req.status === 'fulfilled' || req.status === 'rejected') && (
                              <span className="text-xs text-gray-500 italic">No actions</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t border-gray-700/50">
                <p className="text-sm text-gray-400">
                  Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredRedemptions.length)} of {filteredRedemptions.length} requests
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-md border border-gray-600/50 text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-md border border-gray-600/50 text-gray-400 hover:text-white hover:bg-gray-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default LoyaltyRedemptionsPage;
